import { useState } from 'react';

import { Flex, Text } from '@mantine/core';
import { IconFileTypePdf } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

import {
  useDeletePatientDocumentMutation,
  useLazyGenerateDownloadUrlQuery
} from '@/store/slices/api/patient/patientDocumentsApiSlice';
import formatDate from '@/utils/formatDate';
import IconButton from '@/ui/IconButton';

import styles from './DocumentCard.module.scss';

interface DocumentCardProps {
  document: {
    id: string;
    fileName: string;
    createdAt: string;
  };
  patientId: string;
}

const DocumentCard = ({ document: doc, patientId }: DocumentCardProps) => {
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  const [generateDownloadUrl] = useLazyGenerateDownloadUrlQuery();
  const [deleteDocument] = useDeletePatientDocumentMutation();

  const handleDelete = async (documentId: string) => {
    setDeletingDocId(documentId);
    try {
      await deleteDocument({ patientId, fileId: documentId }).unwrap();
      notifications.show({
        title: 'Success',
        message: 'Document deleted successfully',
        color: 'green'
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete document',
        color: 'red'
      });
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleDownload = async (documentId: string) => {
    setDownloadingDocId(documentId);
    try {
      const response = await generateDownloadUrl(documentId).unwrap();

      // Fetch the file as blob first
      const fileResponse = await fetch(response.downloadUrl);
      const blob = await fileResponse.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Use the downloadFile utility function
      downloadFile(blobUrl, doc.fileName);

      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to download document',
        color: 'red'
      });
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleView = async (documentId: string) => {
    try {
      const response = await generateDownloadUrl(documentId).unwrap();
      window.open(response.downloadUrl, '_blank');
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to open document',
        color: 'red'
      });
    }
  };

  return (
    <div className={styles.documentCard}>
      <div className={styles.documentContent}>
        <div className={styles.documentIcon}>
          <IconFileTypePdf size={22} stroke={1.5} />
        </div>
        <div className={styles.documentDetails}>
          <h3 className={styles.documentTitle}>{doc.fileName}</h3>
          <Text fz={12} c="plum.5" lh={1.2} fw={500}>
            {formatDate(doc.createdAt, 'h:mm A . M/DD/YY')}
          </Text>
        </div>
      </div>
      <Flex align="center">
        <IconButton
          icon="trash"
          loading={deletingDocId === doc.id}
          onClick={() => handleDelete(doc.id)}
          disabled={deletingDocId === doc.id}
        />
        <IconButton
          icon="download"
          loading={downloadingDocId === doc.id}
          onClick={() => handleDownload(doc.id)}
          disabled={downloadingDocId === doc.id}
        />
        <IconButton icon="externalLink" onClick={() => handleView(doc.id)} />
      </Flex>
    </div>
  );
};

const downloadFile = (url: string, filename: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default DocumentCard;
